import type { ApiResponse } from '@shared/types'
import { BackendServiceName } from 'v2/types'
import type {
  RAciBridgeAccessRecords,
  RAciBridgeConnectResponse,
  RContactViewConfigResponse,
  RCustomMcpRegistration,
  RExternalApp,
  RExternalOrgApp,
  ROAuthProvider,
  ROrgAppConnection,
  UpsertContactViewConfigPayload,
} from 'v2/types/api/settings'
import { createService, type Service } from './create-service'
import type { BatchCustomFieldsResponse, CustomField } from './external-apps-service'

export const aciBridgeConnectPath = (appName: string) =>
  `aci/bridge-connect/${encodeURIComponent(appName)}`

export const aciBridgeOAuthCredentialsPath = (appName: string) =>
  `aci/bridge-oauth-credentials/${encodeURIComponent(appName)}`

const ACI_ENDPOINTS = {
  connect: aciBridgeConnectPath,
  oauthProviders: 'aci/bridge-oauth-providers',
  oauthCredentials: aciBridgeOAuthCredentialsPath,
  catalog: 'aci/bridge-catalog',
  linkedAccounts: 'aci/bridge-linked-accounts',
  mcpServers: 'aci/bridge-mcp-servers',
  contactViewConfig: 'aci/bridge-contact-view-config',
}

export interface SetBridgeOAuthCredentialsPayload {
  client_id: string
  client_secret: string
  scope?: string
}

export interface BridgeConnectOptions {
  connection_config?: Record<string, unknown>
  api_key?: string
  username?: string
  password?: string
}

export const buildBridgeConnectPayload = (
  afterRedirectUrl?: string,
  extra?: BridgeConnectOptions,
) => ({
  after_oauth2_link_redirect_url: afterRedirectUrl,
  ...(extra?.connection_config ? { connection_config: extra.connection_config } : {}),
  ...(extra?.api_key ? { api_key: extra.api_key } : {}),
  ...(extra?.username ? { username: extra.username } : {}),
  ...(extra?.password !== undefined ? { password: extra.password } : {}),
})

interface CustomMcpIdentity {
  name: string
  /** Omitted when blank. */
  description?: string
  /** A PNG, WebP or JPEG data URI, small enough to travel inline. Omitted when unset. */
  icon?: string
  url: string
}

interface RegisterStaticCustomMcpPayload extends CustomMcpIdentity {
  auth_type: 'none' | 'bearer' | 'custom_header' | 'basic'
  /** The credential: the bearer token, the custom header's value, or the basic password. */
  token?: string
  /** custom_header only — the bridge rejects it on any other mode. */
  header_name?: string
  /** basic only — the bridge rejects it on any other mode. */
  username?: string
}

interface RegisterOAuthCustomMcpPayload extends CustomMcpIdentity {
  auth_type: 'oauth'
  client_id?: string
  client_secret?: string
  after_oauth2_link_redirect_url: string
  token?: never
}

export type RegisterCustomMcpPayload =
  | RegisterStaticCustomMcpPayload
  | RegisterOAuthCustomMcpPayload

const service = createService('', true, BackendServiceName.externalApps, 'v1')

export const createAciIntegrationsService = (client: Service) => ({
  getOAuthProviders: (): Promise<ApiResponse<ROAuthProvider[]>> =>
    client.get(ACI_ENDPOINTS.oauthProviders),
  setOAuthCredentials: (
    appName: string,
    payload: SetBridgeOAuthCredentialsPayload,
  ): Promise<ApiResponse<void>> => client.post(ACI_ENDPOINTS.oauthCredentials(appName), payload),
  connect: (
    appName: string,
    afterRedirectUrl?: string,
    extra?: BridgeConnectOptions,
  ): Promise<ApiResponse<RAciBridgeConnectResponse>> =>
    client.post(ACI_ENDPOINTS.connect(appName), buildBridgeConnectPayload(afterRedirectUrl, extra)),
  registerCustomMcp: (
    payload: RegisterCustomMcpPayload,
  ): Promise<ApiResponse<RCustomMcpRegistration>> => client.post(ACI_ENDPOINTS.mcpServers, payload),
  getCatalog: (): Promise<ApiResponse<RExternalApp>> => client.get(ACI_ENDPOINTS.catalog),
  getConnections: (): Promise<ApiResponse<RExternalOrgApp[]>> =>
    client.get(ACI_ENDPOINTS.linkedAccounts),
  deleteConnection: (ownerId: string): Promise<ApiResponse<void>> =>
    client.delete(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}`),
  updateConnection: (
    ownerId: string,
    payload: { enabled?: boolean },
  ): Promise<ApiResponse<ROrgAppConnection>> =>
    client.patch(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}`, payload),
  reconnect: (
    ownerId: string,
    afterRedirectUrl?: string,
  ): Promise<ApiResponse<RAciBridgeConnectResponse>> =>
    client.post(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/reconnect`, {
      after_oauth2_link_redirect_url: afterRedirectUrl,
    }),
  getCustomFields: (
    ownerId: string,
    entities: string[],
  ): Promise<ApiResponse<BatchCustomFieldsResponse>> =>
    client.get(
      `${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/custom-fields/${encodeURIComponent(entities.join(','))}`,
    ),
  updateCustomFields: (
    ownerId: string,
    fieldsByEntity: Record<string, CustomField[]>,
  ): Promise<ApiResponse<BatchCustomFieldsResponse>> => {
    const entities = Object.keys(fieldsByEntity).join(',')
    return client.post(
      `${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/custom-fields/${encodeURIComponent(entities)}`,
      fieldsByEntity,
    )
  },
  resyncCustomFields: (
    ownerId: string,
    entities: string[],
  ): Promise<ApiResponse<BatchCustomFieldsResponse>> =>
    client.post(
      `${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/custom-fields/${encodeURIComponent(entities.join(','))}/resync`,
    ),
  grantAccess: (
    ownerId: string,
    payload?: Record<string, unknown>,
  ): Promise<ApiResponse<RAciBridgeAccessRecords>> =>
    client.post(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/grant-access`, {
      users: (payload?.users as string[]) ?? [],
      org: (payload?.share_with_organization as boolean) ?? false,
    }),
  revokeAccess: (
    ownerId: string,
    payload?: Record<string, unknown>,
  ): Promise<ApiResponse<RAciBridgeAccessRecords>> =>
    client.post(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/revoke-access`, {
      user: payload?.user,
      org: payload?.org,
    }),
  getAccessRecords: (ownerId: string): Promise<ApiResponse<RAciBridgeAccessRecords>> =>
    client.get(`${ACI_ENDPOINTS.linkedAccounts}/${encodeURIComponent(ownerId)}/access-records`),
  getContactViewConfig: (): Promise<ApiResponse<RContactViewConfigResponse | null>> =>
    client.get(ACI_ENDPOINTS.contactViewConfig, undefined, {
      validateStatus: (status) => status === 200 || status === 404,
    }),
  upsertContactViewConfig: (
    payload: UpsertContactViewConfigPayload,
  ): Promise<ApiResponse<RContactViewConfigResponse>> =>
    client.post(ACI_ENDPOINTS.contactViewConfig, payload),
})

export const aciIntegrationsService = createAciIntegrationsService(service)
